/** Per-message chrome: filled user bubble, un-bubbled assistant, centered column, Copy, working-files + thinking trail. */

import { createContext, useCallback, useContext, useMemo } from "react"
import { Box, IconButton, Tooltip } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { Check, ContentCopy } from "@mui/icons-material"
import { ChatMessageContent, chatMessageClasses, chatMessageListClasses, chatStreamingIndicatorClasses } from "@mui/x-chat"
import type { ChatMessageContentProps } from "@mui/x-chat"
import { useMessage, useMessageContext } from "@mui/x-chat-headless"
import type { ChatDataMessagePart, ChatMessage, ChatMessagePart, ChatTextMessagePart } from "@mui/x-chat-headless"
import { useCopyToClipboard } from "hooks/use-copy-to-clipboard"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { useIsScheduledWorkflowsEnabled } from "resources/account"
import { renderChatText } from "./chat-text-part"
import { MessageTime } from "./message-time"
import { COMPOSER_CLEARANCE, CONVERSATION_COLUMN_MAX_WIDTH, TRANSCRIPT_CHROME_BAND } from "./composer-chrome"
import { markdownToPlainText } from "./markdown-to-plain-text"
import { EMPTY_WORKING_FILES, WorkingFilesDisclosure, getWorkingFiles } from "./working-files"
import { ThinkingTrail, finalAnswerText, isFinalAnswerOnlyRender } from "./thinking-trail"
import { ArtifactPart } from "./artifact-part"
import { MessageAttachments, attachmentPartsOf, hasBubbleContent } from "./message-attachments"
import { SkillProposalCard } from "./skill-proposal-card"
import type { SkillProposal } from "./skill-proposal-contract"
import type { CreateWorkflowDraft, WorkflowDraft } from "resources/scheduled-workflows/draft-contract"
import { CreateDraftCard } from "./create-draft-card"

const ATTACHMENT_CONTENT_SX = { gridArea: "content", display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 } satisfies SxProps<Theme>

const NO_SKILL_PROPOSALS: Record<string, SkillProposal[]> = {}
const NO_WORKFLOW_DRAFTS: Record<string, WorkflowDraft[]> = {}

// Occupies the grid "content" cell so the trail stacks above the answer.
const TRAIL_CONTENT_SX = { gridArea: "content", display: "flex", flexDirection: "column", minWidth: 0 } satisfies SxProps<Theme>
const FINAL_ANSWER_SX = {
  fontSize: "inherit", lineHeight: 1.5, wordBreak: "break-word",
  // -child, not -of-type: with renderChatText's Fragment wrapper the children are heterogeneous, and -of-type would
  // clamp the first/last of EVERY tag — a lone mid-answer heading or table would lose its margins.
  "& > :first-child": { mt: 0 }, "& > :last-child": { mb: 0 },
  // Match the bubble's plain-code treatment (no chip background/padding) — tool turns, and settled turns with a
  // demoted draft, bypass ChatMessageContent. Inline code only: a fenced block renders as the library's code card,
  // whose own typography this would flatten.
  "& :not(pre) > code": { backgroundColor: "transparent", padding: 0, fontSize: "inherit", fontFamily: "monospace" },
} satisfies SxProps<Theme>

function isDataArtifactPart(part: ChatMessagePart): part is ChatDataMessagePart {
  return part.type === "data-artifact"
}

/** Layout, surface, bubble geometry, and typography on the <ChatBox> root; scheme-aware CSS vars serve both schemes. */
export const MESSAGE_PRESENTATION_SX = {
  [`& .${chatMessageListClasses.scroller}`]: {
    backgroundColor: "var(--app-bg-paper)",
  },
  // Bottom padding clears the measured composer AND the status band above it, so neither hides the last turn.
  [`& .${chatMessageListClasses.content}`]: {
    maxWidth: CONVERSATION_COLUMN_MAX_WIDTH,
    marginInline: "auto",
    width: "100%",
    paddingBlock: `1.5rem calc(${COMPOSER_CLEARANCE} + ${TRANSCRIPT_CHROME_BAND})`,
  },
  [`& .${chatMessageClasses.root}`]: {
    paddingInline: "1rem",
    paddingBlockEnd: "1rem",
  },
  // A CSS reset collapsed the markdown <p> margins; restore vertical rhythm between adjacent paragraphs at the
  // message level so it applies to both the library's markdown path and the tool-trail path.
  // Scoped to sibling <p> only — headings/lists keep their own spacing and aren't double-spaced.
  [`& .${chatMessageClasses.root} p`]: {
    marginBlock: 0,
  },
  [`& .${chatMessageClasses.root} p + p`]: {
    marginBlockStart: "0.75rem",
  },
  [`& .${chatMessageClasses.bubble}`]: {
    fontSize: "inherit",
    lineHeight: 1.5,
    "& :not(pre) > code": { backgroundColor: "transparent", padding: 0, fontSize: "inherit" },
  },
  [`& .${chatMessageClasses.roleUser} .${chatMessageClasses.bubble}`]: {
    backgroundColor: "var(--app-primary-filled)",
    color: "var(--mui-palette-primary-contrastText)",
    borderRadius: "1.125rem",
    padding: "0.625rem 1rem",
    // Shrink-to-fit left a one-word prompt a sliver and squeezed a fence's card header below its content width.
    minWidth: "12rem",
    maxWidth: "75%",
    alignSelf: "flex-end",
  },
  // Assistant: un-bubbled (transparent, no padding/radius), full column width.
  [`& .${chatMessageClasses.roleAssistant} .${chatMessageClasses.bubble}`]: {
    backgroundColor: "transparent",
    padding: 0,
    borderRadius: 0,
    maxWidth: "100%",
    alignSelf: "stretch",
  },
  // One thinking affordance: the library pills the waiting-phase dots (grey fill + radius — a leftover assistant
  // bubble) while the streaming dots inside the message are bare, and indents that row 16px against the messages'
  // 12px, so the dots changed shape and jumped when the answer started.
  [`& .${chatStreamingIndicatorClasses.bubble}`]: {
    backgroundColor: "transparent",
    padding: 0,
    borderRadius: 0,
  },
  [`& .${chatStreamingIndicatorClasses.row}`]: {
    paddingInline: "1rem",
  },
  // Kill the library's "paper" surface on the actions row (fill/elevation/radius) so the timestamp + copy sit
  // flat on the conversation; its padding + icon sizing are left intact.
  [`& .${chatMessageClasses.actions}`]: {
    backgroundColor: "transparent",
    boxShadow: "none",
    borderRadius: 0,
  },
  // Only the last assistant answer's meta stays pinned; every other row (user prompts and earlier answers) keeps
  // the library's hover/focus reveal of the whole meta — timestamp and toolbar together.
  [`& .${chatMessageClasses.actions}:has([data-last-assistant="true"])`]: {
    opacity: 1,
    visibility: "visible",
  },
} satisfies SxProps<Theme>

/** The id of the last assistant message in render order; its toolbar stays visible (see MESSAGE_PRESENTATION_SX). */
export const LastAssistantMessageContext = createContext<string | null>(null)

export const SkillProposalsContext = createContext<Record<string, SkillProposal[]>>(NO_SKILL_PROPOSALS)

export const WorkflowDraftsContext = createContext<Record<string, WorkflowDraft[]>>(NO_WORKFLOW_DRAFTS)

function SkillProposals({ messageId }: { messageId: string | undefined }) {
  const byMessageId = useContext(SkillProposalsContext)
  const proposals = messageId == null ? undefined : byMessageId[messageId]
  if (proposals == null || proposals.length === 0) return null
  return <>{proposals.map((proposal) => <SkillProposalCard key={proposal.proposalId} proposal={proposal} />)}</>
}

function isCreateWorkflowDraft(draft: WorkflowDraft): draft is CreateWorkflowDraft {
  return draft.action === "create"
}

function WorkflowDrafts({ messageId }: { messageId: string | undefined }) {
  const byMessageId = useContext(WorkflowDraftsContext)
  const isEnabled = useIsScheduledWorkflowsEnabled()
  const drafts = messageId == null ? undefined : byMessageId[messageId]
  if (!isEnabled || drafts == null) return null
  return <>{drafts.filter(isCreateWorkflowDraft).map((draft) => <CreateDraftCard key={draft.draftId} draft={draft} />)}</>
}

export function isMessageInFlight(message: ChatMessage): boolean {
  return message.status === "pending" || message.status === "sending" || message.status === "streaming"
}

function isTextPart(part: ChatMessagePart): part is ChatTextMessagePart {
  return part.type === "text"
}

/** The message's text parts joined (user's raw prompt, or assistant response pre-stripping). */
export function getMessageText(message: ChatMessage): string {
  return message.parts.filter(isTextPart).map((part) => part.text).join("\n\n")
}

/** Users copy their raw prompt; assistants copy the answer the transcript rendered, as plaintext (ADR 0028). */
export function getCopyPayload(message: ChatMessage): string {
  if (message.role !== "assistant") return getMessageText(message)
  const rendered = isFinalAnswerOnlyRender(message, isMessageInFlight(message))
    ? finalAnswerText(message)
    : getMessageText(message)
  return markdownToPlainText(rendered)
}

function CopyMessageButton({ message }: { message: ChatMessage }) {
  const { copy, isCopied } = useCopyToClipboard()
  const analytics = useAnalytics()
  const label = message.role === "user" ? "Copy message" : "Copy response"
  const activeLabel = isCopied ? "Copied to clipboard" : label

  // Only a copy that reached the clipboard. `role` is what makes this readable: copying your own prompt back
  // is a different act from taking an answer away, and the second is the one that says the answer was good.
  const onCopy = useCallback(async () => {
    if (!await copy(getCopyPayload(message))) return
    analytics.track(AI_TEAMMATE_EVENTS.MESSAGE_COPIED, { role: message.role })
  }, [copy, message, analytics])
  const handleClick = useCallback(() => { void onCopy() }, [onCopy])

  return (
    <Tooltip title={activeLabel}>
      <IconButton
        size="small"
        aria-label={activeLabel}
        data-testid="chatbox-copy-button"
        onClick={handleClick}
      >
        {isCopied ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
      </IconButton>
    </Tooltip>
  )
}

// display:contents keeps the timestamp + copy as direct flex children of the actions container.
const ACTIONS_SLOT_SX = { display: "contents" } satisfies SxProps<Theme>

/** messageActions slot: a time-ago label plus Copy. The library's actions container governs the hover/focus
 *  reveal; MESSAGE_PRESENTATION_SX pins only the last assistant answer's meta — all others reveal on hover. */
export function MessageChromeActions({ messageId }: { messageId: string }) {
  const message = useMessage(messageId)
  const lastAssistantId = useContext(LastAssistantMessageContext)
  if (message == null) return null
  // No copy mid-flight — the text isn't final.
  if (isMessageInFlight(message)) return null
  const hasText = getMessageText(message).length > 0
  const isLastAssistant = message.role === "assistant" && messageId === lastAssistantId
  if (!hasText && message.createdAt == null) return null
  return (
    <Box component="span" sx={ACTIONS_SLOT_SX} data-last-assistant={isLastAssistant ? "true" : undefined}>
      {message.createdAt != null && <MessageTime date={message.createdAt} />}
      {hasText && <CopyMessageButton message={message} />}
    </Box>
  )
}

/** messageContent slot: appends the working-files disclosure; tool turns hoist steps into the trail above the final answer. */
export function MessageContentWithWorkingFiles({ afterContent, partProps, ...contentProps }: ChatMessageContentProps) {
  const { message, role, streaming } = useMessageContext()
  // Our text renderer wins over the library default (APP-3405); anything else the slot was handed passes through.
  const mergedPartProps = useMemo(
    () => ({ ...partProps, text: { ...partProps?.text, renderText: renderChatText } }),
    [partProps],
  )
  const workingFiles = role === "assistant" && !streaming && message ? getWorkingFiles(message) : EMPTY_WORKING_FILES
  // WorkingFilesDisclosure self-guards on an empty list, so pass the fragment unconditionally.
  const mergedAfterContent = (
    <>
      {afterContent}
      <WorkingFilesDisclosure files={workingFiles} />
      <SkillProposals messageId={message?.id} />
      <WorkflowDrafts messageId={message?.id} />
    </>
  )

  if (role === "assistant" && message != null && isFinalAnswerOnlyRender(message, streaming)) {
    // The answer renders full-style from the first token, so it never jumps on settle (APP-3413).
    const finalText = finalAnswerText(message)
    const artifactParts = message.parts.filter(isDataArtifactPart)
    return (
      <Box sx={TRAIL_CONTENT_SX}>
        <ThinkingTrail message={message} />
        {finalText !== "" && <Box sx={FINAL_ANSWER_SX}>{renderChatText(finalText)}</Box>}
        {artifactParts.map((part, index) => <ArtifactPart key={part.id ?? index} part={part} message={message} index={index} />)}
        {workingFiles.length > 0 && <WorkingFilesDisclosure files={workingFiles} />}
        {/* The final-answer branch returns before mergedAfterContent, so the card is mounted here too —
            without this, every tool-using turn (which is most of them) would stage a skill invisibly. */}
        <SkillProposals messageId={message.id} />
        <WorkflowDrafts messageId={message.id} />
      </Box>
    )
  }

  const attachments = attachmentPartsOf(message)
  if (attachments.length > 0 && message != null) {
    return (
      <Box sx={ATTACHMENT_CONTENT_SX}>
        <MessageAttachments parts={attachments} message={message} />
        {hasBubbleContent(message) && <ChatMessageContent {...contentProps} partProps={mergedPartProps} afterContent={mergedAfterContent} />}
      </Box>
    )
  }

  return <ChatMessageContent {...contentProps} partProps={mergedPartProps} afterContent={mergedAfterContent} />
}
